import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Share2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARD_W = 1080;
const CARD_H = 1920;

// Minimal QR encoder for the platform URL (uses a public QR image service as fallback)
const qrImageUrl = (text) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=0&color=000000&bgcolor=daae3e&data=${encodeURIComponent(text)}`;

const loadImage = (src, timeoutMs = 5000) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let done = false;
    const finish = (val) => { if (!done) { done = true; resolve(val); } };
    img.onload = () => finish(img);
    img.onerror = () => finish(null);
    setTimeout(() => finish(null), timeoutMs);
    img.src = src;
  });

// Tiny native QR fallback — draws a simple framed gold box with the URL printed inside
// when remote QR service is unreachable. Keeps share UX usable offline / under CSP.
const drawFallbackQR = (ctx, x, y, size) => {
  ctx.fillStyle = '#daae3e';
  ctx.fillRect(x, y, size + 24, size + 24);
  ctx.fillStyle = '#000';
  ctx.fillRect(x + 12, y + 12, size, size);
  ctx.fillStyle = '#daae3e';
  ctx.font = '700 18px Inter, system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('SCAN', x + 12 + size / 2, y + 12 + size / 2 - 6);
  ctx.font = '500 14px Inter, system-ui';
  ctx.fillText('app.meybra.com', x + 12 + size / 2, y + 12 + size / 2 + 16);
  ctx.textAlign = 'left';
};

const drawCard = async (ctx, opts) => {
  const { challenge, athlete, rank, score, totalParticipants } = opts;
  const isTime = challenge.scoring_type === 'time';

  // Background — dark with subtle gradient
  const grad = ctx.createLinearGradient(0, 0, 0, CARD_H);
  grad.addColorStop(0, '#0a0a0b');
  grad.addColorStop(0.5, '#1a1410');
  grad.addColorStop(1, '#0a0a0b');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Diagonal gold accents
  ctx.fillStyle = 'rgba(218,174,62,0.08)';
  ctx.beginPath();
  ctx.moveTo(0, 1100);
  ctx.lineTo(CARD_W, 1300);
  ctx.lineTo(CARD_W, 1380);
  ctx.lineTo(0, 1180);
  ctx.closePath();
  ctx.fill();

  // Watermark logo (subtle, behind content)
  const logo = await loadImage('/brand/meybra-logo.png');
  if (logo) {
    ctx.globalAlpha = 0.06;
    const lw = 900;
    const ratio = logo.height / logo.width;
    ctx.drawImage(logo, (CARD_W - lw) / 2, (CARD_H - lw * ratio) / 2, lw, lw * ratio);
    ctx.globalAlpha = 1;
  }

  // Top label
  ctx.fillStyle = '#daae3e';
  ctx.font = '600 30px Inter, system-ui';
  ctx.textAlign = 'center';
  ctx.letterSpacing = '8px';
  ctx.fillText('⚡  MEYBRA  WEEKLY  CHALLENGE  ⚡', CARD_W / 2, 140);

  // Challenge title
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 76px Inter, system-ui';
  ctx.fillText(challenge.title.toUpperCase(), CARD_W / 2, 250);

  // Trophy / badge area
  ctx.font = '160px Inter, system-ui';
  ctx.fillText('🏆', CARD_W / 2, 480);

  // Athlete name
  ctx.fillStyle = '#daae3e';
  ctx.font = '900 96px Inter, system-ui';
  ctx.fillText((athlete?.name || 'Athlete').toUpperCase(), CARD_W / 2, 640);

  // Club
  if (athlete?.club_name) {
    ctx.fillStyle = '#bbbbbb';
    ctx.font = '400 36px Inter, system-ui';
    ctx.fillText(athlete.club_name, CARD_W / 2, 700);
  }

  // Score — the hero number
  ctx.fillStyle = '#ffffff';
  ctx.font = '400 36px Inter, system-ui';
  ctx.fillText('SCORE', CARD_W / 2, 830);

  const scoreLabel = score == null ? '—' : (isTime ? `${Number(score).toFixed(1)}s` : Math.round(score));
  ctx.fillStyle = '#daae3e';
  ctx.font = '900 280px Inter, system-ui';
  ctx.fillText(String(scoreLabel), CARD_W / 2, 1080);

  // Rank
  if (rank) {
    ctx.fillStyle = '#ffffff';
    ctx.font = '400 36px Inter, system-ui';
    ctx.fillText('THIS WEEK', CARD_W / 2, 1180);

    ctx.fillStyle = rank <= 3 ? '#fde047' : '#daae3e';
    ctx.font = '900 120px Inter, system-ui';
    const medal = rank === 1 ? '🥇 #1' : rank === 2 ? '🥈 #2' : rank === 3 ? '🥉 #3' : `#${rank}`;
    ctx.fillText(medal, CARD_W / 2, 1320);

    // Total participants hidden during Founding Beta — "#1 of 2" weakens the brag.
    // Re-enable once we have meaningful weekly volume.
  }

  // Bottom QR + brand block — uses external QR service with 5s timeout, falls back to text frame
  const qr = await loadImage(qrImageUrl('https://app.meybra.com'));
  const qrSize = 220;
  if (qr) {
    ctx.fillStyle = '#daae3e';
    ctx.fillRect(80, CARD_H - 320, qrSize + 24, qrSize + 24);
    ctx.drawImage(qr, 80 + 12, CARD_H - 320 + 12, qrSize, qrSize);
  } else {
    drawFallbackQR(ctx, 80, CARD_H - 320, qrSize);
  }

  // Meybra brand text
  ctx.textAlign = 'left';
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 72px Inter, system-ui';
  ctx.fillText('MEYBRA', 360, CARD_H - 220);
  ctx.fillStyle = '#888888';
  ctx.font = '400 28px Inter, system-ui';
  ctx.fillText('app.meybra.com', 360, CARD_H - 170);
  ctx.fillStyle = '#daae3e';
  ctx.font = '600 30px Inter, system-ui';
  ctx.fillText('#MeybraChallenge', 360, CARD_H - 120);
};

const ShareableCard = ({ challenge, athlete, rank, score, totalParticipants, onClose }) => {
  const { getAuthHeaders } = useAuth();
  const canvasRef = useRef(null);
  const [generating, setGenerating] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      await drawCard(ctx, { challenge, athlete, rank, score, totalParticipants });
      if (cancelled) return;
      const url = canvas.toDataURL('image/png');
      setPreviewUrl(url);
      setGenerating(false);
    };
    render();
    return () => { cancelled = true; };
  }, [challenge, athlete, rank, score, totalParticipants]);

  const trackShare = async (channel) => {
    if (!athlete?.id) return;
    try {
      await axios.post(
        `${API}/challenges/share-event?challenge_id=${challenge.id}&athlete_id=${athlete.id}&channel=${channel}`,
        {},
        { headers: getAuthHeaders() }
      );
    } catch (e) {}
  };

  const downloadPng = async () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `meybra-challenge-${(athlete?.name || 'athlete').replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    trackShare('download');
    toast.success('Card downloaded');
  };

  const nativeShare = async () => {
    if (!previewUrl) return;
    try {
      const res = await fetch(previewUrl);
      const blob = await res.blob();
      const file = new File([blob], 'meybra-challenge.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Meybra Weekly Challenge',
          text: `${athlete?.name} scored ${score} on the Meybra Weekly Challenge — ranked #${rank}! 🔥 #MeybraChallenge`,
        });
        trackShare('native');
      } else {
        downloadPng();
      }
    } catch (e) {
      if (e.name !== 'AbortError') downloadPng();
    }
  };

  const shareWhatsapp = () => {
    const text = `${athlete?.name} scored ${score} on the Meybra Weekly Challenge — ranked #${rank}! 🔥\nCompete at app.meybra.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    trackShare('whatsapp');
  };

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="shareable-card-modal">
      <div className="bg-card border border-primary/30 rounded-sm max-w-md w-full p-6 relative my-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X size={20} />
        </button>
        <h3 className="font-heading text-xl uppercase mb-4 text-center">Share Achievement</h3>

        <div className="bg-black rounded-sm overflow-hidden mb-4 aspect-[9/16] flex items-center justify-center">
          {generating && <Loader2 className="animate-spin text-primary" size={32} />}
          {previewUrl && <img src={previewUrl} alt="Achievement card" className="w-full h-full object-contain" data-testid="card-preview" />}
        </div>

        <canvas ref={canvasRef} width={CARD_W} height={CARD_H} className="hidden" />

        <div className="grid grid-cols-3 gap-2 mb-3">
          <Button onClick={nativeShare} className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-xs font-bold" data-testid="share-native-btn">
            <Share2 size={14} className="mr-1" /> Share
          </Button>
          <Button onClick={shareWhatsapp} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 uppercase tracking-wider text-xs">
            WhatsApp
          </Button>
          <Button onClick={downloadPng} variant="outline" className="border-primary/40 text-primary hover:bg-primary/10 uppercase tracking-wider text-xs" data-testid="share-download-btn">
            <Download size={14} className="mr-1" /> PNG
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center uppercase tracking-widest">
          Optimized 9:16 · Perfect for Instagram Stories & Reels
        </p>
      </div>
    </div>
  );
};

export default ShareableCard;
